import axios, { AxiosInstance, AxiosError } from 'axios';
import { installRestartRetry } from '../utils/restartRetry';
import { getLanguage } from '../utils/locale';
import i18n from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers['Accept-Language'] = getLanguage();
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }

        // Convert error messages to user-friendly Turkish
        const status = error.response?.status;
        const serverMessage = (error.response?.data as any)?.message;

        if (!serverMessage) {
          const statusMessages: Record<number, string> = {
            400: i18n.t('Geçersiz istek. Lütfen bilgileri kontrol edin.'),
            403: i18n.t('Bu işlem için yetkiniz bulunmuyor.'),
            404: i18n.t('Aradığınız kayıt bulunamadı.'),
            408: i18n.t('İstek zaman aşımına uğradı. Lütfen tekrar deneyin.'),
            429: i18n.t('Çok fazla istek gönderildi. Lütfen biraz bekleyin.'),
            500: i18n.t('Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.'),
            502: i18n.t('Sunucu geçici olarak kullanılamıyor. Sayfa yenilendiğinde düzelecektir.'),
            503: i18n.t('Servis şu anda bakımda. Lütfen birkaç dakika sonra tekrar deneyin.'),
            504: i18n.t('Sunucu yanıt vermedi. Lütfen tekrar deneyin.')
          };

          if (status && statusMessages[status]) {
            error.message = statusMessages[status];
          } else if (error.code === 'ERR_NETWORK') {
            error.message = i18n.t('İnternet bağlantınızı kontrol edin.');
          } else if (error.code === 'ECONNABORTED') {
            error.message = i18n.t('İstek zaman aşımına uğradı. Lütfen tekrar deneyin.');
          }
        }

        return Promise.reject(error);
      }
    );
    installRestartRetry(this.api);
  }

  // Auth
  login(username: string, password: string, twoFactorCode?: string, turnstileToken?: string) {
    return this.api.post('/auth/login', { username, password, twoFactorCode, turnstileToken });
  }

  register(data: any) {
    return this.api.post('/auth/register', data);
  }

  logout() {
    return this.api.post('/auth/logout');
  }

  enable2FA() {
    return this.api.post('/auth/enable-2fa');
  }

  verify2FA(code: string) {
    return this.api.post('/auth/verify-2fa', { code });
  }

  disable2FA(password: string, code: string) {
    return this.api.post('/auth/disable-2fa', { password, code });
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.api.post('/auth/change-password', { currentPassword, newPassword });
  }

  // Users
  getProfile() {
    return this.api.get('/users/profile');
  }

  updateProfile(data: any) {
    return this.api.put('/users/profile', data);
  }

  updateLanguage(language: string) {
    return this.api.put('/users/me/language', { language });
  }

  getUsers(params?: any) {
    return this.api.get('/users', { params });
  }

  getUser(id: string) {
    return this.api.get(`/users/${id}`);
  }

  createUser(data: any) {
    return this.api.post('/users', data);
  }

  updateUser(id: string, data: any) {
    return this.api.put(`/users/${id}`, data);
  }

  deleteUser(id: string) {
    return this.api.delete(`/users/${id}`);
  }

  getUserStats() {
    return this.api.get('/users/stats/overview');
  }

  // Customers
  getCustomers(params?: any) {
    return this.api.get('/customers', { params });
  }

  getCustomer(id: string) {
    return this.api.get(`/customers/${id}`);
  }

  createCustomer(data: any) {
    return this.api.post('/customers', data);
  }

  updateCustomer(id: string, data: any) {
    return this.api.put(`/customers/${id}`, data);
  }

  deleteCustomer(id: string) {
    return this.api.delete(`/customers/${id}`);
  }

  importCustomers(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.api.post('/customers/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  // WhatsApp Contacts
  getWhatsAppContacts(params?: any) {
    return this.api.get('/whatsapp-contacts', { params });
  }

  getWhatsAppContact(id: string) {
    return this.api.get(`/whatsapp-contacts/${id}`);
  }

  createWhatsAppContact(data: any) {
    return this.api.post('/whatsapp-contacts', data);
  }

  updateWhatsAppContact(id: string, data: any) {
    return this.api.put(`/whatsapp-contacts/${id}`, data);
  }

  deleteWhatsAppContact(id: string) {
    return this.api.delete(`/whatsapp-contacts/${id}`);
  }

  unsubscribeCustomer(id: string, reason?: string) {
    return this.api.post(`/customers/${id}/unsubscribe`, { reason });
  }

  resubscribeCustomer(id: string) {
    return this.api.post(`/customers/${id}/resubscribe`);
  }

  blockCustomer(id: string, reason?: string) {
    return this.api.post(`/customers/${id}/block`, { reason });
  }

  unblockCustomer(id: string) {
    return this.api.post(`/customers/${id}/unblock`);
  }

  getCustomerMessages(id: string, params?: any) {
    return this.api.get(`/customers/${id}/messages`, { params });
  }

  addCustomerTags(id: string, tags: string[]) {
    return this.api.post(`/customers/${id}/tags`, { tags });
  }

  removeCustomerTags(id: string, tags: string[]) {
    return this.api.delete(`/customers/${id}/tags`, { data: { tags } });
  }

  // Campaigns
  getCampaigns(params?: any) {
    return this.api.get('/campaigns', { params });
  }

  getCampaign(id: string) {
    return this.api.get(`/campaigns/${id}`);
  }

  createCampaign(data: any) {
    return this.api.post('/campaigns', data);
  }

  updateCampaign(id: string, data: any) {
    return this.api.put(`/campaigns/${id}`, data);
  }

  deleteCampaign(id: string) {
    return this.api.delete(`/campaigns/${id}`);
  }

  executeCampaign(id: string) {
    return this.api.post(`/campaigns/${id}/execute`);
  }

  pauseCampaign(id: string) {
    return this.api.post(`/campaigns/${id}/pause`);
  }

  resumeCampaign(id: string) {
    return this.api.post(`/campaigns/${id}/resume`);
  }

  getCampaignStats(id: string) {
    return this.api.get(`/campaigns/${id}/stats`);
  }

  getCampaignMessages(id: string, params?: any) {
    return this.api.get(`/campaigns/${id}/messages`, { params });
  }

  previewCampaignAudience(id: string) {
    return this.api.get(`/campaigns/${id}/preview-audience`);
  }

  testCampaign(id: string, testNumbers: string[]) {
    return this.api.post(`/campaigns/${id}/test`, { test_numbers: testNumbers });
  }

  getCampaignTemplates() {
    return this.api.get('/campaigns/templates');
  }

  // Messages
  getMessages(params?: any) {
    return this.api.get('/messages', { params });
  }

  getMessage(id: string) {
    return this.api.get(`/messages/${id}`);
  }

  sendMessage(data: any) {
    return this.api.post('/messages/send', data);
  }

  sendBulkMessages(data: any) {
    return this.api.post('/messages/send-bulk', data);
  }

  getConversation(customerId: string, params?: any) {
    return this.api.get(`/messages/conversations/${customerId}`, { params });
  }

  getMessageStats() {
    return this.api.get('/messages/stats/overview');
  }

  retryMessage(id: string) {
    return this.api.post(`/messages/${id}/retry`);
  }

  // Requests (Talepler)
  getRequests(params?: any) {
    return this.api.get('/requests', { params });
  }

  getRequestStats() {
    return this.api.get('/requests/stats');
  }

  getRequestById(id: string) {
    return this.api.get(`/requests/${id}`);
  }

  createRequest(data: any) {
    return this.api.post('/requests', data);
  }

  updateRequest(id: string, data: any) {
    return this.api.put(`/requests/${id}`, data);
  }

  deleteRequest(id: string) {
    return this.api.delete(`/requests/${id}`);
  }

  updateRequestStatus(id: string, status: string) {
    return this.api.patch(`/requests/${id}/status`, { status });
  }

  updateRequestPriority(id: string, priority: string) {
    return this.api.patch(`/requests/${id}/priority`, { priority });
  }

  // Google Ads API
  getGoogleAdsAccountStats(params?: any) {
    return this.api.get('/google-ads/accounts/stats', { params });
  }

  getGoogleAdsAccounts() {
    return this.api.get('/google-ads/accounts');
  }

  getGoogleAdsCampaigns(params?: any) {
    return this.api.get('/google-ads/campaigns', { params });
  }

  getGoogleAdsTopCampaigns(params?: any) {
    return this.api.get('/google-ads/campaigns/top', { params });
  }

  getGoogleAdsCampaignById(id: string) {
    return this.api.get(`/google-ads/campaigns/${id}`);
  }

  getGoogleAdsAdGroups(campaignId: string, params?: any) {
    return this.api.get(`/google-ads/campaigns/${campaignId}/ad-groups`, { params });
  }

  getGoogleAdsKeywords(adGroupId: string, params?: any) {
    return this.api.get(`/google-ads/ad-groups/${adGroupId}/keywords`, { params });
  }

  getGoogleAdsPerformance(params: any) {
    return this.api.get('/google-ads/performance', { params });
  }

  updateGoogleAdsCampaignStatus(campaignId: string, status: string) {
    return this.api.put(`/google-ads/campaigns/${campaignId}/status`, { status });
  }

  // Google Ads - Campaign Management (RMF C.10, M.10, C.120)
  createGoogleAdsCampaign(data: any) {
    return this.api.post('/google-ads/campaigns', data);
  }

  updateGoogleAdsCampaign(id: string, data: any) {
    return this.api.put(`/google-ads/campaigns/${id}`, data);
  }

  updateGoogleAdsCampaignBudget(campaignId: string, budget: number, budgetType?: string) {
    return this.api.put(`/google-ads/campaigns/${campaignId}/budget`, { budget, budget_type: budgetType });
  }

  // Google Ads - Ad Group Management (RMF C.190)
  createGoogleAdsAdGroup(data: any) {
    return this.api.post('/google-ads/ad-groups', data);
  }

  updateGoogleAdsAdGroupStatus(id: string, status: string) {
    return this.api.put(`/google-ads/ad-groups/${id}/status`, { status });
  }

  // Google Ads - Keyword Management (RMF C.260, C.270, M.140)
  addGoogleAdsKeywords(data: any) {
    return this.api.post('/google-ads/keywords', data);
  }

  addGoogleAdsNegativeKeywords(campaignId: string, keywords: string[]) {
    return this.api.post('/google-ads/negative-keywords', { campaign_id: campaignId, negative_keywords: keywords });
  }

  updateGoogleAdsKeywordStatus(id: string, status: string) {
    return this.api.put(`/google-ads/keywords/${id}/status`, { status });
  }

  // Google Ads - Ad Management (RMF M.130, R.40)
  getGoogleAdsAds(adGroupId: string, params?: any) {
    return this.api.get(`/google-ads/ad-groups/${adGroupId}/ads`, { params });
  }

  createGoogleAdsAd(data: any) {
    return this.api.post('/google-ads/ads', data);
  }

  updateGoogleAdsAdStatus(id: string, status: string) {
    return this.api.put(`/google-ads/ads/${id}/status`, { status });
  }

  // Google Ads - Conversion Tracking (RMF C.65)
  createGoogleAdsConversion(data: any) {
    return this.api.post('/google-ads/conversions', data);
  }

  // Google Ads - Reporting (RMF R.70)
  getGoogleAdsSearchTerms(params?: any) {
    return this.api.get('/google-ads/search-terms', { params });
  }

  // Google Ads - All Ad Groups (MCC support)
  getGoogleAdsAllAdGroups(params?: any) {
    return this.api.get('/google-ads/all-ad-groups', { params });
  }

  // Google Ads - All Keywords (MCC support)
  getGoogleAdsAllKeywords(params?: any) {
    return this.api.get('/google-ads/all-keywords', { params });
  }

  // Google Ads - All Ads (MCC support)
  getGoogleAdsAllAds(params?: any) {
    return this.api.get('/google-ads/all-ads', { params });
  }

  // Google Ads - All Search Terms (synced DB data)
  getGoogleAdsAllSearchTerms(params?: any) {
    return this.api.get('/google-ads/all-search-terms', { params });
  }

  // Google Ads - Extensions (RMF C.75)
  getGoogleAdsExtensions(params?: any) {
    return this.api.get('/google-ads/extensions', { params });
  }

  getGoogleAdsExtensionById(id: string) {
    return this.api.get(`/google-ads/extensions/${id}`);
  }

  createGoogleAdsExtension(data: any) {
    return this.api.post('/google-ads/extensions', data);
  }

  updateGoogleAdsExtension(id: string, data: any) {
    return this.api.put(`/google-ads/extensions/${id}`, data);
  }

  updateGoogleAdsExtensionStatus(id: string, status: string) {
    return this.api.put(`/google-ads/extensions/${id}/status`, { status });
  }

  deleteGoogleAdsExtension(id: string) {
    return this.api.delete(`/google-ads/extensions/${id}`);
  }

  // Templates
  get(url: string, config?: any) {
    return this.api.get(url, config);
  }

  post(url: string, data?: any, config?: any) {
    return this.api.post(url, data, config);
  }

  put(url: string, data?: any, config?: any) {
    return this.api.put(url, data, config);
  }

  delete(url: string, config?: any) {
    return this.api.delete(url, config);
  }

  patch(url: string, data?: any, config?: any) {
    return this.api.patch(url, data, config);
  }

  // =================== GOOGLE ADS OAUTH & SYNC ===================

  async getGoogleAdsOAuthUrl(): Promise<{ success: boolean; data: { url: string } }> {
    const response = await this.api.get('/google-ads/oauth/authorize');
    return response.data;
  }

  async getGoogleAdsOAuthStatus(): Promise<{ success: boolean; data: { connected: boolean; accounts: any[] } }> {
    const response = await this.api.get('/google-ads/oauth/status');
    return response.data;
  }

  async disconnectGoogleAds(accountId: number): Promise<{ success: boolean; disconnected?: number; message?: string }> {
    const response = await this.api.post(`/google-ads/oauth/disconnect/${accountId}`);
    return response.data;
  }

  async syncGoogleAdsAccount(accountId: number): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post(`/google-ads/sync/${accountId}`);
    return response.data;
  }

  async syncAllGoogleAdsAccounts(): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post('/google-ads/sync');
    return response.data;
  }

  async toggleGoogleAdsSyncEnabled(accountId: number): Promise<{ success: boolean; sync_enabled: boolean }> {
    const response = await this.api.put(`/google-ads/accounts/${accountId}/sync-toggle`);
    return response.data;
  }

  async getGoogleAdsSyncProgress(): Promise<{ success: boolean; progress: any }> {
    const response = await this.api.get('/google-ads/sync/progress');
    return response.data;
  }

  // Dashboard CRUD
  getDashboards() { return this.api.get('/google-ads/dashboards'); }
  getDashboard(id: number) { return this.api.get(`/google-ads/dashboards/${id}`); }
  createDashboard(data: any) { return this.api.post('/google-ads/dashboards', data); }
  updateDashboard(id: number, data: any) { return this.api.put(`/google-ads/dashboards/${id}`, data); }
  deleteDashboard(id: number) { return this.api.delete(`/google-ads/dashboards/${id}`); }
  duplicateDashboard(id: number) { return this.api.post(`/google-ads/dashboards/${id}/duplicate`); }
  setDefaultDashboard(id: number) { return this.api.put(`/google-ads/dashboards/${id}/set-default`); }

  // AI Reports
  compareAIReports(reportId1: string, reportId2: string) { return this.api.post('/google-ads/ai-reports/compare', { reportId1, reportId2 }); }
  getAIReportTokenStats() { return this.api.get('/google-ads/ai-reports/token-stats'); }
  generateAIReport(data: { startDate: string; endDate: string; reportType: string; accountIds?: number[] }) { return this.api.post('/google-ads/ai-reports/generate', data); }
  getAIReports() { return this.api.get('/google-ads/ai-reports'); }
  getAIReport(id: string) { return this.api.get(`/google-ads/ai-reports/${id}`); }
  deleteAIReport(id: string) { return this.api.delete(`/google-ads/ai-reports/${id}`); }
  chatWithAIReport(reportId: string, message: string) { return this.api.post(`/google-ads/ai-reports/${reportId}/chat`, { message }); }

  // Report Editor CRUD
  getReports() { return this.api.get('/google-ads/reports'); }
  getReport(id: number) { return this.api.get(`/google-ads/reports/${id}`); }
  createReport(data: any) { return this.api.post('/google-ads/reports', data); }
  updateReport(id: number, data: any) { return this.api.put(`/google-ads/reports/${id}`, data); }
  deleteReport(id: number) { return this.api.delete(`/google-ads/reports/${id}`); }
  duplicateReport(id: number) { return this.api.post(`/google-ads/reports/${id}/duplicate`); }
  queryReport(data: any) { return this.api.post('/google-ads/reports/query', data); }
}

export default new ApiService();